import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { MultiSegmentedControl } from 'cng-common/components/segmentedControl'
import { FormMultiselect } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { Static } from 'core-components/types'
import { Crud } from 'core/types'
import { GeoMapping } from 'partner360/components/filters'
import { KEY_INITIATIVE_STATUSES } from 'partner360/configs/keyInitiatives/keyInitiativeStatusesConfig'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'
import {
  filtersInitialData,
  KEY_INITIATIVE_FILTERS_FORM,
} from 'partner360/features/keyInitiatives/configs/config'
import { DashboardLabel } from 'partner360/features/keyInitiatives/ui/common/dashboardLabel/DashboardLabel'
import { getBusinessPriorityNames } from 'partner360/features/keyInitiatives/ui/helpers/getBusinessPriorityNames'

import { TRANSLATION_KEYS } from '../../configs/translations'
import { useKeyInitiativesFiltersForm } from '../hooks/useKeyInitiativesFiltersForm'

import { transformKeyObjectivesToOptions } from './helpers/transformKeyObjectivesToOptions'

import styles from './KeyInitiativesFilters.module.scss'

interface IKeyInitiativesFilters {
  programType: ProgramTypes
  isRollupMode: boolean
  data: TKeyInitiative[]
  onClose: () => void
}

export const KeyInitiativesFilters = ({
  programType,
  isRollupMode,
  data,
  onClose,
}: IKeyInitiativesFilters) => {
  const { keyObjectives, kiBusinessPriorities } = useSelector(selectJBPSettings)
  const { resetFormData, quickFilter, statuses, countryCodes, changeStatuses, changeCountryCodes } =
    useKeyInitiativesFiltersForm()

  const keyObjectivesOptionsSource = useMemo(() => {
    const transformed = transformKeyObjectivesToOptions({ keyObjectives, programType, data })
    return new Crud({
      fetcher: new Static(transformed),
      keyField: 'key',
      defaultPaging: {
        page: 0,
        pageSize: transformed.length,
      },
    })
  }, [data, keyObjectives, programType])

  const kiBusinessPrioritiesSource = useMemo(() => {
    const businessPriorities = [
      ...new Set(
        data.flatMap((item) =>
          getBusinessPriorityNames(kiBusinessPriorities, item.BusinessPriorities)
        )
      ),
    ]
      .sort()
      .map((value) => ({
        key: value,
        label: value,
      }))

    return new Crud({
      fetcher: new Static(businessPriorities),
      keyField: 'key',
      defaultPaging: {
        page: 0,
        pageSize: data.length,
      },
    })
  }, [data, kiBusinessPriorities])

  const onResetFilters = useCallback(() => {
    resetFormData()
    onClose()
  }, [onClose, resetFormData])

  if (quickFilter) {
    return null
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.field}>
          <DashboardLabel translationKey={TRANSLATION_KEYS.dashboard.filters.strategicInitiative} />

          <FormMultiselect
            defaultValue={filtersInitialData.strategicInitiatives}
            field={'strategicInitiatives'}
            formKey={KEY_INITIATIVE_FILTERS_FORM}
            source={keyObjectivesOptionsSource}
            caption={'label'}
            isStatic
            disabled={Boolean(quickFilter)}
          />
        </div>

        <div className={styles.field}>
          <DashboardLabel translationKey={TRANSLATION_KEYS.dashboard.filters.businessPriority} />

          <FormMultiselect
            defaultValue={filtersInitialData.businessPriorities}
            field={'businessPriorities'}
            formKey={KEY_INITIATIVE_FILTERS_FORM}
            source={kiBusinessPrioritiesSource}
            caption={'label'}
            isStatic
            disabled={Boolean(quickFilter)}
          />
        </div>

        <RenderWhen condition={isRollupMode}>
          <div className={styles.field}>
            <DashboardLabel translationKey={TRANSLATION_KEYS.dashboard.filters.geo} />
            <GeoMapping
              className={styles.fields__field}
              values={countryCodes}
              onSelectChange={(_t, _x, values) => {
                changeCountryCodes(values)
              }}
              typeOfSelectedNodes={'onlyLeavesItems'}
              disabled={Boolean(quickFilter)}
            />
          </div>
        </RenderWhen>
        <div className={cn(styles.field, styles.status)}>
          <DashboardLabel translationKey={TRANSLATION_KEYS.dashboard.filters.status} />

          <MultiSegmentedControl
            selectedItems={statuses}
            onMultiItemSelect={changeStatuses}
            useItemKeys
            isDisabled={Boolean(quickFilter)}
          >
            {KEY_INITIATIVE_STATUSES.map((el) => (
              <MultiSegmentedControl.Item key={el.key}>
                <Translation text={getKeyInitiativeStatusTranslationKey(el.key)} />
              </MultiSegmentedControl.Item>
            ))}
          </MultiSegmentedControl>
        </div>
      </div>

      <Flex className={styles.btnWrapper} justifyContent={'flex-end'}>
        <Button color={'danger'} onClick={onResetFilters}>
          <Translation text={TRANSLATION_KEYS.dashboard.filters.resetFilters} />
        </Button>
        <Button color={'grey'} onClick={onClose}>
          <Translation text={TRANSLATION_KEYS.dashboard.filters.close} />
        </Button>
      </Flex>
    </div>
  )
}
